import type { ComposerAttributeNode } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';

export type PresetSelection = NonNullable<ComposerAttributeNode['presetSelection']>;
export type PresetMode = 'macrocategories' | 'categories' | 'compositions';

export const emptyPresetSelection = (): PresetSelection => ({ macrocategories: [], categories: [], compositions: [] });

export const getPresetMode = (selection?: PresetSelection): PresetMode | 'mixed' | null => {
    const modes = (['macrocategories', 'categories', 'compositions'] as const).filter((mode) => selection?.[mode]?.length);
    return modes.length > 1 ? 'mixed' : (modes[0] ?? null);
};

type Macro = { macroCategoryID: number; name: string | null; category: readonly ({ categoryID: number; name: string | null } | null)[] | null };
type Composition = { compositionID: number; categories?: readonly ({ categoryID: number } | null)[] | null };
export type PresetOrigin = { mode: PresetMode; name: string };

// Resolve only the selected source type. Persist source IDs, never the expanded list:
// this keeps category-based inclusion dynamic and manual inclusion static.
export const getPresetOrigins = (selection: PresetSelection, composition: Composition, macros: readonly Macro[]): PresetOrigin[] => {
    const mode = getPresetMode(selection);
    if (!mode || mode === 'mixed') return [];
    if (mode === 'compositions') return selection.compositions.includes(composition.compositionID) ? [{ mode, name: '' }] : [];
    const categoryIDs = new Set((composition.categories ?? []).flatMap((category) => (category ? [category.categoryID] : [])));
    if (mode === 'macrocategories')
        return macros
            .filter(
                (macro) =>
                    selection.macrocategories.includes(macro.macroCategoryID) &&
                    macro.category?.some((category) => category && categoryIDs.has(category.categoryID))
            )
            .map((macro) => ({ mode, name: macro.name ?? String(macro.macroCategoryID) }));
    return selection.categories.flatMap((selected) => {
        const category = macros
            .find((macro) => macro.macroCategoryID === selected.macroCategoryID)
            ?.category?.find((item) => item?.categoryID === selected.categoryID);
        return category && categoryIDs.has(selected.categoryID) ? [{ mode, name: category.name ?? String(selected.categoryID) }] : [];
    });
};
